import type React from "react";
import { useContext, useMemo } from "react";
import { useSWRConfig } from "swr";
import { ScheduleInputNavigation } from "~/components/common/construction_schedule/ScheduleInputNavigation";
import { ReportImageNavigation } from "~/components/common/reports/navigation/ReportImageNavigation";
import { OwnStatusHandleButtons } from "~/components/common/statuses/OwnStatusHandleButtons";
import { UploadFileNavigation } from "~/components/common/upload_files/UploadFileNavigation";
import { useDealContext } from "~/context/DealContext";
import { useDealCompletionReportImages } from "~/hooks/deals/useDealCompletionReportImages";
import { dealCacheKey } from "~/hooks/useDeals";
import type { DealUploadFile } from "~/models/DealUploadFile";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import {
	OWN_AWAITING_ACCEPTANCE,
	OWN_IN_CONSTRUCTION,
	OWN_SEND_ESTIMATE,
	ownStatusMovingValidation
} from "~/models/Status";
import { PATH as COMPLETE_REPORT_PATH } from "~/pages/deals/[id]/completion_reports";
import {
	getDownloadPath,
	create as uploadReceiptFile
} from "~/utils/api/deals/DealReceiptFile";
import {
	destroy as destroyReceiptFile,
	getFilePath
} from "~/utils/api/deals/DealUploadedFile";
import Styles from "./statuses.module.scss";

export const OwnInConstruction: React.FC = () => {
	const account = useContext(ItandiAccountContext);
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const { data: completeImages } = useDealCompletionReportImages().index(
		deal.id
	);

	const statusDisabled = useMemo(
		() => !ownStatusMovingValidation[OWN_IN_CONSTRUCTION](account),
		[account]
	);

	const handleDestroyReceipt = async (file: DealUploadFile) => {
		return destroyReceiptFile(deal.id, file.id);
	};

	const handleUploadReceipt = async (file: File) =>
		uploadReceiptFile(file, deal.id);

	const handleCreateReceipt = async () => uploadReceiptFile(null, deal.id);

	const handleGetReceiptPath = (file: DealUploadFile): string =>
		getFilePath(deal.id, file);

	const downloadExcel = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};

	if (completeImages === null) return null;

	return (
		<div>
			<h3 className={Styles.Title}>工事中です</h3>
			<p className={Styles.Description}>
				工事完了後にステータスを進めてください
			</p>
			<UploadFileNavigation
				deal={deal}
				downloadExcel={downloadExcel}
				files={deal.dealReceiptFiles}
				fileType="工事発注書・請書"
				handleCreate={handleCreateReceipt}
				handleDestroy={handleDestroyReceipt}
				handleGetFilePath={handleGetReceiptPath}
				handleMutate={async () => {
					await globalMutate(dealCacheKey(deal.id));
				}}
				handleUpload={handleUploadReceipt}
				title="工事発注書・請書を登録してください"
			/>
			<ScheduleInputNavigation deal={deal} />
			<ReportImageNavigation
				detailPageButtonLabel="画像登録画面"
				detailPagePath={COMPLETE_REPORT_PATH(deal.id)}
				optionalLabel
				reportImages={completeImages}
				title="完了報告画像を登録してください"
			/>
			<OwnStatusHandleButtons
				backwardDisabled={statusDisabled}
				forwardDisabled={statusDisabled}
				nextStatusId={OWN_AWAITING_ACCEPTANCE}
				prevStatusId={OWN_SEND_ESTIMATE}
			/>
		</div>
	);
};
